/**
 * FeedbackForm — In-app feedback inbox.
 * Anyone (signed-in or anonymous) can submit; admins read via /admin tools.
 * Lightweight & cost-free alternative to a WhatsApp-to-developer flow.
 */
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MessageSquarePlus, Send, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';

const CATEGORIES = [
  { value: 'bug', label: '🐞 Bug / something broken' },
  { value: 'idea', label: '💡 Idea / feature request' },
  { value: 'love', label: '❤️ What I love' },
  { value: 'other', label: '💬 Other' },
];

interface FeedbackFormProps {
  /** Override the page_url tag (defaults to current location) — useful when embedded inside /profile or other in-app surfaces */
  defaultPageUrl?: string;
}

export function FeedbackForm({ defaultPageUrl }: FeedbackFormProps = {}) {
  const { user } = useAuth();
  const [category, setCategory] = useState('idea');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);


  const submit = async () => {
    const trimmed = message.trim();
    if (trimmed.length < 5) {
      toast.error('Please write a few more words so we can help.');
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.from('app_feedback').insert({
        user_id: user?.id ?? null,
        // Guests are welcome to send feedback — tag it so admins can triage it.
        category: user ? category : `guest_${category}`,
        message: trimmed,
        page_url: defaultPageUrl ?? (typeof window !== 'undefined' ? window.location.href : null),
        app_version: 'v1.0',
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 300) : null,
      });
      if (error) throw error;
      setSubmitted(true);
      setMessage('');
    } catch (e: any) {
      logger.error('Feedback submit failed', e);
      toast.error(e.message || 'Could not send feedback — try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquarePlus className="h-5 w-5" />
          Share feedback with the team
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {submitted ? (
          <div className="flex items-start gap-3 rounded-md bg-emerald-50 border border-emerald-200 p-4">
            <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-emerald-900">Thanks — we read every message.</p>
              <p className="text-emerald-800">
                We're building GoalDigger with you. Reply count or follow-up depends on the volume, but every note shapes what we ship next.
              </p>
              <Button variant="link" className="px-0 h-auto mt-1 text-emerald-700"
                onClick={() => setSubmitted(false)}>
                Send another →
              </Button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Found a bug? Have an idea? Tell us — it goes straight to the team.
              {!user && ' Sign-in not required.'}
            </p>
            <div className="space-y-2">
              <Label htmlFor="fb-cat">What's it about?</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger id="fb-cat"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="fb-msg">Your message</Label>
              <Textarea
                id="fb-msg"
                placeholder="What happened, what you'd like, or just say hi…"
                rows={4}
                maxLength={2000}
                value={message}
                onChange={e => setMessage(e.target.value)}
              />
              <p className="text-xs text-muted-foreground text-right">{message.length} / 2000</p>
            </div>
            {user && (
              <p className="text-[11px] text-muted-foreground">
                Your name and email will be visible to the GoalDigger admin team so we can follow up if needed.
              </p>
            )}
            <Button onClick={submit} disabled={submitting || message.trim().length < 5} className="gap-2">
              <Send className="h-4 w-4" />
              {submitting ? 'Sending…' : 'Send feedback'}
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default FeedbackForm;
